import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const services = [
  'Website Development',
  'E-commerce Development',
  'Mobile App Development',
  'Custom Software Development',
  'SEO Services',
  'Digital Marketing',
  'UI/UX Design',
  'Business Consulting',
];

const budgets = [
  'Under $5,000',
  '$5,000 - $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  'Above $50,000',
];

export default function ConsultationPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: '',
    preferred_date: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/consultation`, formData);
      toast.success('Consultation booked successfully! We\'ll contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        budget: '',
        message: '',
        preferred_date: '',
      });
    } catch (error) {
      toast.error('Failed to book consultation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Free Consultation - FuturePath Technologies</title>
        <meta name="description" content="Book a free consultation with FuturePath Technologies. Discuss your project requirements and get expert advice." />
      </Helmet>

      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="consultation-hero-title">
              Book Free Consultation
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Let's discuss your project and explore how we can help you achieve your goals.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="consultation-form-section">
        <div className="container-custom max-w-3xl">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit}
            className="glass-card p-8 md:p-12 rounded-3xl"
            data-testid="consultation-form"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Calendar size={24} className="text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-outfit text-white">
                Consultation Details
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Your Name *</label>
                <Input
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="consultation-name-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Email Address *</label>
                <Input
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="consultation-email-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Phone Number *</label>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="+91 1234567890"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="consultation-phone-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Company Name</label>
                <Input
                  type="text"
                  name="company"
                  placeholder="Your Company"
                  value={formData.company}
                  onChange={handleChange}
                  data-testid="consultation-company-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Service Interested In *</label>
                <Select
                  value={formData.service}
                  onValueChange={(value) => handleSelectChange('service', value)}
                  required
                >
                  <SelectTrigger data-testid="consultation-service-select" className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10">
                    {services.map((service) => (
                      <SelectItem key={service} value={service} className="text-white hover:bg-white/10">
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Preferred Date</label>
                <Input
                  type="date"
                  name="preferred_date"
                  value={formData.preferred_date}
                  onChange={handleChange}
                  data-testid="consultation-date-input"
                  className="bg-white/5 border-white/10 text-white focus:border-primary [color-scheme:dark]"
                />
              </div>

              {/* <div className="md:col-span-2">
                <label className="text-sm text-muted-foreground mb-2 block">Preferred Date</label>
                <Input
                  type="date"
                  name="preferred_date"
                  value={formData.preferred_date}
                  onChange={handleChange}
                  data-testid="consultation-date-input"
                  className="bg-white/5 border-white/10 text-white focus:border-primary"
                />
              </div> */}

              <div className="md:col-span-2">
                <label className="text-sm text-muted-foreground mb-2 block">Project Details *</label>
                <Textarea
                  name="message"
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="consultation-message-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-primary resize-none"
                />
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full rounded-full bg-primary hover:bg-primary/90 neon-glow mt-8"
              data-testid="consultation-submit-btn"
            >
              {loading ? 'Booking...' : 'Book Consultation'}
              <Send className="ml-2" size={20} />
            </Button>
          </motion.form>
        </div>
      </section>
    </>
  );
}
